import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { inboundApi } from '@/features/inbound/ux/data/inbound.api';
import { BUDGET_BANDS } from '@shared/backend/inbound';
import type { BudgetBand } from '@shared/web/types';

interface Draft {
  name: string;
  company: string;
  email: string;
  budgetBand: BudgetBand | null;
  message: string;
  /** Honeypot. Hidden, never focusable, always empty from a real browser. */
  website: string;
}

const EMPTY: Draft = {
  name: '',
  company: '',
  email: '',
  budgetBand: null,
  message: '',
  website: '',
};

const MEETING_URL = 'https://calendar.app.google/YJYEeaJUnceo782t8';
const MEETING_EMBED_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2LK9fGO76NltjIsq5BxXp5OKyV2ofB8q813kHCu2NcmGpGV6GkYb8m3CIVMZIt1BJbLiXKmG8I?gv=true';

/**
 * The enquiry form on the marketing page.
 *
 * ── Why four fields and not twelve ──────────────────────────────────────────
 *
 * Every extra field on a contact form loses people who were going to be worth
 * talking to. These four are the minimum needed to answer the only question
 * this form exists to support — "is this worth a call?" — and the brief is
 * optional because the ones who have something to say will write it anyway
 * and the ones who do not would have typed "hi" to get past a required field.
 *
 * ── Why budget is a band and not a number ───────────────────────────────────
 *
 * Asking for a figure asks a brand to commit to a number they have not agreed
 * internally, and the honest answer is usually a shrug — so they either
 * abandon the form or invent something. Four ranges are answerable in one
 * click by someone who only roughly knows, which is everyone at this stage.
 */
export function ContactForm() {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (isSending) return;

    /**
     * Validated here as a courtesy, and again on the server as the actual
     * rule. `required` attributes alone would let the browser choose the
     * wording and the placement of the message; these are the two cases worth
     * saying something specific about.
     */
    if (!draft.name.trim() || !draft.company.trim() || !draft.email.trim()) {
      setError('Name, brand and email, and we can take it from there.');
      return;
    }
    if (!draft.budgetBand) {
      setError('Pick a budget range so we know how to plan.');
      return;
    }

    setError(null);
    setIsSending(true);
    try {
      await inboundApi.submit({
        name: draft.name,
        company: draft.company,
        email: draft.email,
        budgetBand: draft.budgetBand,
        message: draft.message.trim() || null,
        // Where they came from, if the browser will say. Read here rather than
        // from the Referer header server-side — by the time the POST fires,
        // that header is our own page.
        referrer: document.referrer || null,
        website: draft.website,
      });
      setSent(true);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Something went wrong. Email hello@a2.agency and we will pick it up there.',
      );
    } finally {
      setIsSending(false);
    }
  }

  /**
   * The form is replaced rather than cleared.
   *
   * A form that empties itself is the classic "did that send?" moment, and the
   * usual fix — a toast above a still-editable form — invites a second submit
   * from anyone who does not notice it. Taking the fields away makes the
   * outcome unambiguous.
   */
  if (sent) {
    return (
      <div className="border-line bg-cream-2 rounded-xl border px-4 py-6 sm:p-6 md:p-8">
        <div className="border-rust/30 text-rust grid size-10 place-items-center rounded-full border">
          <Check aria-hidden="true" className="size-5" />
        </div>
        <h3
          ref={(node) => node?.focus()}
          tabIndex={-1}
          className="font-display mt-5 text-3xl focus:outline-none"
        >
          Book a meeting.
        </h3>
        <p className="text-clay mt-3 leading-relaxed">
          Your note about <span className="text-ink">{draft.company.trim()}</span> is with us.
          {' '}Pick a time below to talk through your project.
        </p>
        <div className="border-line bg-cream mt-6 overflow-hidden rounded-lg border">
          <iframe
            src={MEETING_EMBED_URL}
            title="Book a meeting with A2 Agency"
            className="block h-[600px] w-full border-0"
          />
        </div>
        <p className="text-clay mt-4 text-center text-xs">
          Prefer a separate tab?{' '}
          <a
            href={MEETING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink hover:text-rust rounded-sm underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust"
          >
            Open the booking page
          </a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="border-line bg-cream-2 rounded-xl border p-6 md:p-8">
      <p className="marketing-label text-ink">Start a project</p>

      <div className="mt-6 grid gap-5">
        <Field label="Your name">
          <input
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            autoComplete="name"
            placeholder="Alex Rivera"
            className={INPUT}
          />
        </Field>

        <Field label="Brand or company">
          <input
            value={draft.company}
            onChange={(event) => set('company', event.target.value)}
            autoComplete="organization"
            placeholder="Northwind Supply Co."
            className={INPUT}
          />
        </Field>

        <Field label="Work email">
          <input
            type="email"
            value={draft.email}
            onChange={(event) => set('email', event.target.value)}
            autoComplete="email"
            inputMode="email"
            placeholder="alex@northwind.com"
            className={INPUT}
          />
        </Field>

        {/*
          Radios drawn as a two-by-two grid of buttons, not a <select>.
          The whole set of options is visible without a click, which is what
          makes this the fastest field on the form rather than the one people
          stall on. Native inputs underneath, so keyboard and screen readers
          get real radio-group semantics for free.
        */}
        <fieldset>
          <legend className="text-clay text-[13px] font-medium">
            Campaign budget
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {BUDGET_BANDS.map((band) => {
              const checked = draft.budgetBand === band.value;
              return (
                <label
                  key={band.value}
                  className={[
                    'cursor-pointer rounded-lg border px-3 py-3 text-sm transition-colors duration-200',
                    'focus-within:ring-rust/30 focus-within:ring-2',
                    checked
                      ? 'border-rust bg-rust/5 text-ink'
                      : 'border-line text-clay hover:border-clay/40 bg-cream',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="budgetBand"
                    value={band.value}
                    checked={checked}
                    onChange={() => set('budgetBand', band.value)}
                    className="sr-only"
                  />
                  {band.label}
                </label>
              );
            })}
          </div>
        </fieldset>

        <Field label="What are you working on?" optional>
          <textarea
            value={draft.message}
            onChange={(event) => set('message', event.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Launching a new AI product, looking for creators who reach our audience."
            className={`${INPUT} resize-none`}
          />
        </Field>

        {/*
          Honeypot. Hidden from people three ways — off-screen, aria-hidden and
          removed from the tab order — while staying a perfectly ordinary,
          tempting input to a script reading the DOM. `autoComplete="off"` so a
          browser's form filler does not fill it in and get a real visitor
          silently dropped.
        */}
        <div aria-hidden className="pointer-events-none absolute -left-[9999px] opacity-0">
          <label>
            Website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={draft.website}
              onChange={(event) => set('website', event.target.value)}
            />
          </label>
        </div>
      </div>

      {/*
        The error sits directly above the button that produced it, and is
        announced. A message at the top of a long form is a message nobody
        scrolls back up to find.
      */}
      {error && (
        <p role="alert" className="text-rust mt-5 text-sm">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isSending}
        className="marketing-button bg-ink text-cream hover:bg-rust focus-visible:ring-rust/40 mt-6 w-full focus-visible:ring-2 disabled:opacity-60"
      >
        {isSending ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Sending
          </>
        ) : (
          <>
            Send it over
            <ArrowRight className="size-4" />
          </>
        )}
      </button>

      <p className="text-clay mt-4 text-center text-xs">
        No list, no newsletter. We only use this to reply.
      </p>
    </form>
  );
}

const INPUT =
  'border-line bg-cream text-ink placeholder:text-clay/70 focus:border-rust focus:ring-rust/20 mt-2 w-full rounded-lg border px-3 py-2.5 text-sm transition-colors duration-200 focus:ring-2 focus:outline-none';

function Field({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-clay text-[13px] font-medium">
        {label}
        {/*
          Optional is marked, required is not — the inverse of the usual
          asterisk convention. On a form this short, marking the one field you
          can skip is less visual noise than marking the four you cannot.
        */}
        {optional && <span className="text-clay/60 normal-case"> · optional</span>}
      </span>
      {children}
    </label>
  );
}
